import React, { useEffect, useRef, useState } from 'react';
import styles from './NexsetSchemaViewer.module.scss';
import { Nexset } from 'new/entities/Nexset';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import {
  GET_DATA_CHARACTERISTICS_REQUEST,
  GET_DATA_SAMPLES_REQUEST,
} from 'js/constants/actionTypes';
import { fetchCharacteristics, fetchDataSamples } from 'js/actions';
import { useDistance } from 'new/hooks/useDistance';
import RecipeBuilderGrid from 'js/components/recipeBuilder/RecipeBuilderGrid';
import { RecipeBuilderControls } from 'new/elements/NexsetSchemaViewer/RecipeBuilderControls';
import { useRecipeBuilderData } from 'new/elements/NexsetSchemaViewer/useRecipeBuilderData';
import { DATA_CHARS_ENABLED } from 'js/constants/config';
import {
  SCHEMA_DISPLAY_LIVE_SAMPLES_DEFAULT,
  SCHEMA_DISPLAY_NUM_SAMPLES_DEFAULT,
} from 'js/constants/schemaEditor';
import useDidUpdateEffect from 'new/hooks/useDidUpdateEffect';
import { getSemanticSchema } from 'new/utils/DataSetUtil/getSemanticSchema';
import useAppDispatch from 'js/hooks/useAppDispatch';

interface NexsetSchemaViewerProps {
  data: Nexset;
  hideTitle?: boolean;
}

export const NexsetSchemaViewer = (props: NexsetSchemaViewerProps) => {
  const { data, hideTitle = false } = props;
  const tabsRef = useRef();
  const loading = useIsLoading(GET_DATA_SAMPLES_REQUEST, GET_DATA_CHARACTERISTICS_REQUEST);
  const dispatch = useAppDispatch();
  const { schema, samples, recipeGroups, transforms, nestedSamples, meta } =
    useRecipeBuilderData(data);
  const [delayGrid, setDelayGrid] = useState(true);
  const [selectedSample, setSelectedSample] = useState<{
    count: number;
    live: boolean;
    value?: string;
  }>({
    value: undefined,
    count: SCHEMA_DISPLAY_NUM_SAMPLES_DEFAULT,
    live: SCHEMA_DISPLAY_LIVE_SAMPLES_DEFAULT,
  });
  const [view, setView] = useState();
  const [search, setSearch] = useState('');
  const [showMeta, setShowMeta] = useState(false);
  const height = useDistance(tabsRef, 500);

  useEffect(() => {
    dispatch(fetchDataSamples(data.id, selectedSample.count, selectedSample.live));
  }, []);

  useDidUpdateEffect(() => {
    if (!loading) {
      setDelayGrid(false);
    }
  }, [loading]);

  const onRefresh = () => {
    dispatch(fetchDataSamples(data.id, selectedSample.count, selectedSample.live));
    if (DATA_CHARS_ENABLED) {
      dispatch(fetchCharacteristics(data.id));
    }
  };

  const onSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const onSamplesChange = (sample: any) => {
    setSelectedSample(sample);
    dispatch(fetchDataSamples(data.id, sample.count, sample.live));
  };

  const onViewControlChange = (e, value) => {
    setView(value);
  };

  const onShowMetaChanged = (showMeta) => {
    setShowMeta(showMeta);
  };

  if (!height) {
    return null;
  }

  const semanticSchema = getSemanticSchema(data?.semantic_schema);

  return (
    <div style={{ height, width: '100%' }} className={styles.wrapper}>
      {!delayGrid && (
        <>
          {!hideTitle && <h2 ref={tabsRef}>Schema & Samples</h2>}
          <RecipeBuilderControls
            nexset={data}
            onRefresh={onRefresh}
            onSearchChange={onSearchChange}
            onSamplesChange={onSamplesChange}
            numSamplesValue={selectedSample?.value}
            onViewControlChange={onViewControlChange}
            viewControlValue={view}
            hideTitle={hideTitle}
          />
          <RecipeBuilderGrid
            dataSet={data}
            searchText={search}
            selectedViewMode={view}
            numSamples={selectedSample?.count}
            liveSamples={selectedSample?.live}
            isLoading={loading}
            showCharacteristics
            showMeta={showMeta}
            onShowMetaChanged={onShowMetaChanged}
            schema={schema}
            metaSchema={meta}
            nestedSamples={nestedSamples}
            samples={samples}
            transforms={transforms}
            recipeGroups={recipeGroups}
            semanticSchema={semanticSchema}
          />
        </>
      )}
    </div>
  );
};
